import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { useBalance } from 'wagmi';
import { faArrowRight } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { CopyAddress } from '@components/CopyAddress';
import {
  EthereumIndicator,
  OptimismIndicator,
} from '@components/Song/MintComponents/NetworkIndicator/ChainIndicators';
import { Button } from '@components/main-components/button/Button';
import { Modal } from '@components/main-components/modals/Modal';
import { useAuth } from '@context/auth/base';
import { ModalContext, ModalType } from '@context/modal';
import { trackClickEvent } from '@lib/analytics';
import { AVAILABLE_CHAIN_IDS } from '@utils/constants';
import { formatWeiToEthForDisplay, humanReadableChainName } from '@utils/helpers';
import { BridgeModal } from './Bridge';
import { OnRampModal } from './OnRamp';

const ETH_GAS_FEE_ONLY = 'You need ETH to pay for gas fees.';

const svgStyling =
  'w-5 [&_svg]:w-5 [&_svg]:h-5  md:w-6 md:[&_svg]:w-6 md:[&_svg]:h-6  [&_svg]:aspect-square flex items-center justify-center';

export const EthWhiteLogo = () => (
  <div className={clsx('[&_path]:fill-black  [&_rect]:fill-white', svgStyling)}>
    <EthereumIndicator />
  </div>
);

export const InsufficientFundsModal = ({
  description,
  chainId,
  primaryButton,
  secondaryButton,
}: {
  description: React.ReactNode;
  chainId: number;
  primaryButton: React.ReactNode;
  secondaryButton: React.ReactNode;
}) => {
  const { closeCancelModal } = ModalContext.useContainer();
  const { authUserAddress } = useAuth();

  const {
    data: balanceData,
    isError: balanceError,
    isLoading: balanceLoading,
  } = useBalance({
    address: authUserAddress || '0x000',
    enabled: !!authUserAddress,
    formatUnits: 'gwei',
    chainId,
    staleTime: 0,
  });

  const body = (
    <div className="flex w-full flex-col items-center gap-y-6">
      <p className="font-base text-base-m">{description}</p>

      <div className="flex w-full flex-col items-center gap-y-4 font-base">
        <div className="flex w-full items-start justify-between align-top">
          <div className="flex flex-col justify-between gap-y-0.5">
            <h2 className="text-base-l font-medium">Your Balance</h2>
            <p className="text-base-s text-base500">{humanReadableChainName(chainId)}</p>
          </div>
          <div className="flex flex-col justify-between gap-y-0.5 ">
            <div className="font-title text-title-s font-medium ">
              {balanceLoading || balanceError ? (
                <Button variant="text" size="XS" label="" loading />
              ) : balanceData ? (
                <>
                  {formatWeiToEthForDisplay({
                    weiValue: balanceData?.value.toString(),
                    maxDecimals: 4,
                  })}{' '}
                  ETH
                </>
              ) : (
                <p className="text-base-s text-base500">Unable to load</p>
              )}
            </div>
            {authUserAddress && (
              <p className="text-right text-base-s text-base500">
                <CopyAddress publicAddress={authUserAddress?.toLowerCase()} />
              </p>
            )}
          </div>
        </div>

        <div className="flex w-full flex-col gap-y-3">
          {primaryButton}
          {secondaryButton}
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      header={<div className="flex items-center">Insufficient Funds</div>}
      body={body}
      closeModal={closeCancelModal}
    />
  );
};

export const InsufficientFundsOptimism = ({
  wei,
  actionButton,
  type,
}: {
  wei: string;
  actionButton: React.ReactNode;
  type: 'Pay with ETH on Mainnet' | 'Pay with Credit Card';
}) => {
  const { authUserAddress } = useAuth();
  const { data: mainnetBalance } = useBalance({
    address: authUserAddress,
    chainId: AVAILABLE_CHAIN_IDS.eth,
  });
  const { openModal } = ModalContext.useContainer();

  const hasMainnetBalance = mainnetBalance && mainnetBalance.value !== 0n;

  const formattedETH = formatWeiToEthForDisplay({
    weiValue: wei,
    maxDecimals: 4,
  });
  const { closeCancelModal } = ModalContext.useContainer();

  const gasFeeOnly = formattedETH === '0';

  const subText = gasFeeOnly ? (
    <>
      You need ETH on Optimism to pay for gas fees. {type} to receive this release on Optimism, or
      bridge your ETH to Optimism.
    </>
  ) : (
    <>
      You need at least <strong>{formattedETH} ETH (plus gas fees) on Optimism</strong>. {type} to
      receive this release on Optimism, or bridge your ETH to Optimism.
    </>
  );

  const bridgeIcon = (
    <div className="flex items-center justify-center gap-0.5 text-base-m text-base400">
      <span className={clsx('[&_rect]:fill-base400', svgStyling)}>
        <EthereumIndicator />
      </span>
      <FontAwesomeIcon icon={faArrowRight} />

      <span className={clsx('[&_rect]:fill-base800', svgStyling)}>
        <OptimismIndicator />
      </span>
    </div>
  );

  return (
    <InsufficientFundsModal
      description={subText}
      chainId={AVAILABLE_CHAIN_IDS.optimism}
      primaryButton={actionButton}
      secondaryButton={
        <>
          {hasMainnetBalance && (
            <Button
              label="Bridge ETH to Optimism"
              variant="tertiary"
              fullWidth
              trailingIcon={bridgeIcon}
              className="flex items-center justify-between "
              onClick={() => {
                trackClickEvent({
                  event: 'Bridge ETH to Optimism Clicked',
                  properties: {
                    sourceComponent: 'Insufficient Funds Modal',
                  },
                });
                closeCancelModal();
                openModal(ModalType.BRIDGE_MODAL, {
                  body: <BridgeModal />,
                });
              }}
            />
          )}
        </>
      }
    />
  );
};

export const InsufficientFundsEth = ({
  wei,
  actionButton,
}: {
  wei: string;
  actionButton: React.ReactNode;
}) => {
  const formattedETH = formatWeiToEthForDisplay({
    weiValue: wei,
    maxDecimals: 4,
  });
  const { openModal } = ModalContext.useContainer();

  const gasFeeOnly = formattedETH === '0';

  const subText = gasFeeOnly ? (
    ETH_GAS_FEE_ONLY
  ) : (
    <>
      You need at least <strong>{formattedETH} ETH (plus gas fees)</strong>. Buy with a credit card
      or buy more ETH.
    </>
  );

  return (
    <InsufficientFundsModal
      description={subText}
      chainId={AVAILABLE_CHAIN_IDS.eth}
      primaryButton={actionButton}
      secondaryButton={
        <Button
          label="Add Funds"
          variant="tertiary"
          fullWidth
          trailingIcon={<EthWhiteLogo />}
          className="flex items-center justify-between"
          onClick={() => {
            openModal(ModalType.ON_RAMP, {
              body: <OnRampModal />,
            });
          }}
        />
      }
    />
  );
};

export const InsufficientFundsToDeployOptimism = ({ checkFunds }: { checkFunds: boolean }) => {
  const { authUserAddress } = useAuth();
  const { data: opBalance, isLoading } = useBalance({
    address: authUserAddress,
    chainId: AVAILABLE_CHAIN_IDS.optimism,
  });
  const bridgeIcon = (
    <div className="flex items-center justify-center gap-0.5 text-base-m text-base400">
      <EthWhiteLogo />
      <FontAwesomeIcon icon={faArrowRight} />

      <div className={clsx('[&_path]:fill-black  [&_rect]:fill-white', svgStyling)}>
        <OptimismIndicator />
      </div>
    </div>
  );

  const { openModal, closeCancelModal } = ModalContext.useContainer();

  /**
   * We have fetched OP Balance  and user does not have OP balance
   * This helps us avoid showing the funds modal when user has bridged ETH
   */
  if (checkFunds && opBalance && !isLoading && !Boolean(opBalance && opBalance.value === 0n))
    return null;

  return (
    <InsufficientFundsModal
      description={
        <>
          You need ETH on Optimism to pay for gas fees to upload your release to the Optimism
          blockchain.
          <p className="mt-2">
            You can Bridge ETH to Optimism (if you have ETH on Mainnet) or Add Funds via card.
          </p>
        </>
      }
      chainId={AVAILABLE_CHAIN_IDS.optimism}
      primaryButton={
        <Button
          label="Bridge ETH to Optimism"
          variant="primary"
          fullWidth
          trailingIcon={bridgeIcon}
          className="flex items-center justify-between"
          onClick={() => {
            trackClickEvent({
              event: 'Bridge ETH to Optimism Clicked',
              properties: {
                sourceComponent: 'Insufficient Funds Modal',
              },
            });
            closeCancelModal();
            openModal(ModalType.BRIDGE_MODAL, {
              body: <BridgeModal />,
            });
          }}
        />
      }
      secondaryButton={
        <Button
          label="Add Funds"
          variant="tertiary"
          fullWidth
          trailingIcon={<OptimismIndicator />}
          className="flex items-center justify-between"
          onClick={() => {
            openModal(ModalType.ON_RAMP, {
              body: <OnRampModal />,
            });
          }}
        />
      }
    />
  );
};
